import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  MenuItem,
  Chip,
  InputAdornment,
  TextareaAutosize,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { notificationsAPI, classesAPI } from '../../services/api';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target_role: 'all',
    target_class: '',
    priority: 'normal',
    expiry_date: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [notifRes, classRes] = await Promise.all([
        notificationsAPI.getAll(),
        classesAPI.getAll(),
      ]);
      setNotifications(notifRes.data);
      setClasses(classRes.data);
    } catch (error) {
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenDialog = (notification = null) => {
    if (notification) {
      setEditingId(notification.id);
      setFormData({
        title: notification.title,
        message: notification.message,
        target_role: notification.target_role,
        target_class: notification.target_class || '',
        priority: notification.priority,
        expiry_date: notification.expiry_date,
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        message: '',
        target_role: 'all',
        target_class: '',
        priority: 'normal',
        expiry_date: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    if (!formData.title || !formData.message) {
      showSnackbar('Title and Message are required', 'error');
      return;
    }

    try {
      if (editingId) {
        await notificationsAPI.update(editingId, formData);
        showSnackbar('Notification updated successfully', 'success');
      } else {
        await notificationsAPI.create(formData);
        showSnackbar('Notification created successfully', 'success');
      }
      fetchAllData();
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Error saving notification', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;

    try {
      await notificationsAPI.delete(id);
      showSnackbar('Notification deleted successfully', 'success');
      fetchAllData();
    } catch (error) {
      showSnackbar('Error deleting notification', 'error');
    }
  };

  const filteredNotifications = notifications.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'error',
      normal: 'default',
      low: 'success',
    };
    return colors[priority] || 'default';
  };

  const getTargetLabel = (notification) => {
    if (notification.target_role === 'all') return 'All Users';
    if (notification.target_class) return `${notification.target_role} - Class ${notification.target_class}`;
    return notification.target_role.charAt(0).toUpperCase() + notification.target_role.slice(1);
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Toolbar sx={{ paddingLeft: 0, paddingRight: 0, marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ marginRight: 2 }}
        >
          Send Notification
        </Button>

        <TextField
          placeholder="Search by title or message..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 350 }}
        />
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Title</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell sx={{ width: 200 }}>Message Preview</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredNotifications.length > 0 ? (
                filteredNotifications.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ opacity: isExpired(item.expiry_date) ? 0.6 : 1 }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{item.title}</TableCell>
                    <TableCell>{getTargetLabel(item)}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.priority}
                        size="small"
                        color={getPriorityColor(item.priority)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-IN') : 'No Expiry'}
                      {isExpired(item.expiry_date) && (
                        <Chip
                          label="Expired"
                          size="small"
                          variant="filled"
                          sx={{ marginLeft: 1, height: 20 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.message}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: 3 }}>
                    No notifications found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Notification' : 'Send New Notification'}</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
            placeholder="Notification title"
          />

          <TextareaAutosize
            minRows={4}
            maxRows={6}
            placeholder="Notification message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value,
              })
            }
            style={{
              width: '100%',
              padding: '8px',
              fontFamily: 'Arial',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginBottom: '16px',
            }}
          />

          <TextField
            select
            fullWidth
            label="Target Audience"
            name="target_role"
            value={formData.target_role}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="admin">Administrators</MenuItem>
            <MenuItem value="teacher">Teachers</MenuItem>
            <MenuItem value="student">Students</MenuItem>
            <MenuItem value="parent">Parents</MenuItem>
          </TextField>

          {formData.target_role !== 'all' && (
            <TextField
              select
              fullWidth
              label="Specific Class (Optional)"
              name="target_class"
              value={formData.target_class}
              onChange={handleInputChange}
              sx={{ marginBottom: 2 }}
            >
              <MenuItem value="">All Classes</MenuItem>
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.standard}>
                  {c.display_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            fullWidth
            label="Priority"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Expiry Date (Optional)"
            name="expiry_date"
            type="date"
            value={formData.expiry_date}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
